import React from 'react';
import { Alert, Grid, Stack } from '@mui/material';
import PlaylistAddCheckOutlinedIcon from '@mui/icons-material/PlaylistAddCheckOutlined';

import ProgrammeCardsPanel from './ProgrammeCardsPanel';
import AxisSummaryPanel from './AxisSummaryPanel';
import StatusSummaryPanel from './StatusSummaryPanel';
import ProgrammeReviewGrid from './ProgrammeReviewGrid';
import ActivityProgrammeFilterBar from './ActivityProgrammeFilterBar';

import {
  ActivityProgrammeFilters,
  ActivityProgrammeRow,
  AxisSummary,
  ProgrammeCards,
  StatusSummary,
} from './activityprogramme.models';

import {
  bulkTransitionProgrammeRows,
  fetchAxisSummary,
  fetchProgrammeCards,
  fetchProgrammeReview,
  fetchStatusSummary,
  notifyFilteredProgrammeRows,
  notifyProgrammeRows,
  transitionProgrammeRow,
} from './activityprogramme.apis';

import activityProgrammeLanguage from './activityprogramme.language';
import { ComboRow } from '../core/coremodels';
import { fetchCombo } from '../core/coreapi';
import { PlanningPageProps, PlanningWorkbenchShell } from '../planningcore';
import { fetchPlanningJourneyPhases } from '../core/planningJourney.cache';
import FPlanningJourney from '../core/FPlanningJourney';

interface State {
  costcenter: string;
  axis: string;
  status: string;
  costcenters: ComboRow[];
  axes: ComboRow[];
  statuses: ComboRow[];
  cards: ProgrammeCards | null;
  axesummaries: AxisSummary[];
  statussummaries: StatusSummary[];
  rows: ActivityProgrammeRow[];
  selectedIds: number[];
  loading: boolean;
  error: string;
  success: string;
  phases: any[];
}

export default class ActivityProgrammeReviewWorkbench extends React.Component<PlanningPageProps, State> {
  state: State = {
    costcenter: '',
    axis: '',
    status: '',
    costcenters: [],
    axes: [],
    statuses: [],
    cards: null,
    axesummaries: [],
    statussummaries: [],
    rows: [],
    selectedIds: [],
    loading: false,
    error: '',
    success: '',
    phases: [],
  };

  componentDidMount() {
    void this.initialize();
  }

  componentDidUpdate(previous: PlanningPageProps) {
    if (Number(previous.plancontext?.pci || 0) !== Number(this.props.plancontext?.pci || 0)) {
      void this.initialize();
    }
  }

  private planningCycleId = () => Number(this.props.plancontext?.pci || 0);

  private filters = (): ActivityProgrammeFilters => ({
    pci: this.planningCycleId(),
    ccc: this.state.costcenter || null,
    axc: this.state.axis || null,
    stc: this.state.status || null,
  });

  private initialize = async () => {
    if (!this.planningCycleId()) {
      this.setState({ error: 'Planning Cycle context is missing.' });
      return;
    }

    this.setState({ loading: true, error: '', success: '' });
    try {
      const [phases, costcenters, axes, statuses] = await Promise.all([
        fetchPlanningJourneyPhases(),
        fetchCombo('costcenter'),
        fetchCombo('axis'),
        fetchCombo('activityplanstate'),
      ]);
      this.setState({ phases, costcenters, axes, statuses });
      await this.load();
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ loading: false });
    }
  };

  private notice = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    this.props.showNotification?.(message, severity);
    if (!this.props.showNotification) {
      this.setState({
        error: severity === 'error' ? message : '',
        success: severity === 'success' ? message : '',
      });
    }
  };

  private load = async () => {
    this.setState({ loading: true, error: '' });
    try {
      const filters = this.filters();
      const [cards, axesummaries, statussummaries, rows] = await Promise.all([
        fetchProgrammeCards(filters),
        fetchAxisSummary(filters),
        fetchStatusSummary(filters),
        fetchProgrammeReview(filters, 0, 1000),
      ]);
      this.setState({ cards, axesummaries, statussummaries, rows, selectedIds: [] });
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ loading: false });
    }
  };

  private clear = () =>
    this.setState(
      { costcenter: '', axis: '', status: '', selectedIds: [], error: '', success: '' },
      () => void this.load(),
    );

  private rowId = (row: ActivityProgrammeRow) => row.activityplanid;

  private notifyResponsible = async (row: ActivityProgrammeRow) => {
    try {
      const result: any = await notifyProgrammeRows([this.rowId(row)], 'GENERAL');
      this.notice(`${result?.queued || 1} notification queued.`);
    } catch (error: any) {
      this.notice(error?.message || String(error), 'error');
    }
  };

  private notifyIncompleteResponsible = async (row: ActivityProgrammeRow) => {
    try {
      const result: any = await notifyProgrammeRows([this.rowId(row)], 'INCOMPLETE');
      this.notice(`${result?.queued || 1} incomplete-plan notification queued.`);
    } catch (error: any) {
      this.notice(error?.message || String(error), 'error');
    }
  };

  private notifyFiltered = async (incompleteOnly: boolean, type: 'GENERAL' | 'INCOMPLETE') => {
    try {
      const result: any = await notifyFilteredProgrammeRows(this.filters(), incompleteOnly, type);
      this.notice(`${result?.queued || 0} notification(s) queued.`);
    } catch (error: any) {
      this.notice(error?.message || String(error), 'error');
    }
  };

  private transition = async (row: ActivityProgrammeRow, action: 'COMPLETE' | 'CERTIFY' | 'RETURN') => {
    const reason = action === 'RETURN' ? window.prompt('Reason for returning this Activity Plan:') || '' : undefined;
    if (action === 'RETURN' && !reason) return;

    try {
      const result = await transitionProgrammeRow(row, action, reason);
      if (!result.success) {
        this.notice(result.message || 'Transition rejected.', 'warning');
        return;
      }
      this.notice(`Activity Plan ${action.toLowerCase()}d.`);
      await this.load();
    } catch (error: any) {
      this.notice(error?.message || String(error), 'error');
    }
  };

  private bulkTransition = async (action: 'COMPLETE' | 'CERTIFY') => {
    try {
      const result = await bulkTransitionProgrammeRows(this.state.selectedIds, action);
      this.notice(
        `${result.processed || 0} processed; ${result.failed || 0} failed.`,
        result.failed ? 'warning' : 'success',
      );
      await this.load();
    } catch (error: any) {
      this.notice(error?.message || String(error), 'error');
    }
  };

  render() {
    const state = this.state;
    return (
      <PlanningWorkbenchShell
        {...this.props}
        onBack={this.props.onBack}
        headerExtension={
          <FPlanningJourney
            phases={state.phases}
            currentPhaseCode={activityProgrammeLanguage.currentPhaseCode}
          />
        }
        title={this.props.title || activityProgrammeLanguage.title}
        subtitle={activityProgrammeLanguage.subtitle}
        icon={<PlaylistAddCheckOutlinedIcon />}
        loading={state.loading}
        error={state.error}
        success={state.success}
        onRefresh={this.load}
        filters={
          <ActivityProgrammeFilterBar
            costcenter={state.costcenter}
            axis={state.axis}
            status={state.status}
            costcenters={state.costcenters}
            axes={state.axes}
            statuses={state.statuses}
            onCostcenterChange={(costcenter) => this.setState({ costcenter })}
            onAxisChange={(axis) => this.setState({ axis })}
            onStatusChange={(status) => this.setState({ status })}
            onRefresh={this.load}
            onClear={this.clear}
            loading={state.loading}
          />
        }
      >
        {state.error && <Alert severity="error" sx={{ mb: 2 }}>{state.error}</Alert>}
        <Stack spacing={3}>
          <ProgrammeCardsPanel cards={state.cards} />
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, lg: 7 }}><AxisSummaryPanel rows={state.axesummaries} /></Grid>
            <Grid size={{ xs: 12, lg: 5 }}><StatusSummaryPanel rows={state.statussummaries} /></Grid>
          </Grid>
          <ProgrammeReviewGrid
            rows={state.rows}
            loading={state.loading}
            selectedIds={state.selectedIds}
            onSelectionChange={(selectedIds) => this.setState({ selectedIds })}
            onNotifyResponsible={this.notifyResponsible}
            onNotifyIncompleteResponsible={this.notifyIncompleteResponsible}
            onNotifyAllIncomplete={() => this.notifyFiltered(true, 'INCOMPLETE')}
            onNotifyAllResponsible={() => this.notifyFiltered(false, 'GENERAL')}
            onComplete={(row) => this.transition(row, 'COMPLETE')}
            onCertify={(row) => this.transition(row, 'CERTIFY')}
            onReturn={(row) => this.transition(row, 'RETURN')}
            onBulkComplete={() => this.bulkTransition('COMPLETE')}
            onBulkCertify={() => this.bulkTransition('CERTIFY')}
          />
        </Stack>
      </PlanningWorkbenchShell>
    );
  }
}
